/**
 * Состояние сессии.
 *
 * Access-токен хранится только в памяти: перезагрузка страницы его теряет,
 * и сессия восстанавливается запросом `POST /auth/refresh` по HttpOnly cookie
 * (см. `SessionProvider`). Это осознанный компромисс — один дополнительный
 * запрос при загрузке в обмен на то, что XSS не даёт долговременного доступа
 * (ADR 0006).
 *
 * Идентификатор активного пространства, наоборот, сохраняется в localStorage:
 * это не секрет, а пользовательское удобство — после перезагрузки человек
 * остаётся в том же пространстве, а не «сбрасывается» в первое по алфавиту.
 */

import { create } from 'zustand'

import type { Permission, User, WorkspaceMembershipSummary } from '@/lib/types'

const ACTIVE_WORKSPACE_KEY = 'reportflow.activeWorkspaceId'

function readStoredWorkspaceId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY)
  } catch {
    // Приватный режим браузера может запрещать localStorage
    return null
  }
}

function storeWorkspaceId(workspaceId: string | null): void {
  try {
    if (workspaceId) localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId)
    else localStorage.removeItem(ACTIVE_WORKSPACE_KEY)
  } catch {
    // Игнорируем: активное пространство просто не сохранится между сессиями
  }
}

interface AuthState {
  accessToken: string | null
  user: User | null
  workspaces: WorkspaceMembershipSummary[]
  activeWorkspaceId: string | null
  /** Идёт ли восстановление сессии при загрузке приложения. */
  isRestoring: boolean

  setAccessToken: (token: string) => void
  setSession: (user: User, workspaces: WorkspaceMembershipSummary[]) => void
  setWorkspaces: (workspaces: WorkspaceMembershipSummary[]) => void
  setActiveWorkspace: (workspaceId: string) => void
  setRestoring: (value: boolean) => void
  clearSession: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  workspaces: [],
  activeWorkspaceId: readStoredWorkspaceId(),
  isRestoring: true,

  setAccessToken: (token) => set({ accessToken: token }),

  setSession: (user, workspaces) => {
    const stored = get().activeWorkspaceId
    // Сохранённое пространство могло быть удалено или доступ к нему отозван —
    // тогда выбираем первое доступное, а не оставляем неработающий идентификатор
    const isStoredAvailable = workspaces.some((item) => item.workspace_id === stored)
    const active = isStoredAvailable ? stored : (workspaces[0]?.workspace_id ?? null)

    storeWorkspaceId(active)
    set({ user, workspaces, activeWorkspaceId: active, isRestoring: false })
  },

  setWorkspaces: (workspaces) => {
    const stored = get().activeWorkspaceId
    const isStoredAvailable = workspaces.some((item) => item.workspace_id === stored)
    const active = isStoredAvailable ? stored : (workspaces[0]?.workspace_id ?? null)
    storeWorkspaceId(active)
    set({ workspaces, activeWorkspaceId: active })
  },

  setActiveWorkspace: (workspaceId) => {
    storeWorkspaceId(workspaceId)
    set({ activeWorkspaceId: workspaceId })
  },

  setRestoring: (value) => set({ isRestoring: value }),

  clearSession: () => {
    storeWorkspaceId(null)
    set({
      accessToken: null,
      user: null,
      workspaces: [],
      activeWorkspaceId: null,
      isRestoring: false,
    })
  },
}))

/** Активное пространство целиком (роль и права нужны интерфейсу). */
export function useActiveWorkspace(): WorkspaceMembershipSummary | null {
  return useAuthStore((state) => {
    const { workspaces, activeWorkspaceId } = state
    if (!activeWorkspaceId) return null
    return workspaces.find((item) => item.workspace_id === activeWorkspaceId) ?? null
  })
}

/**
 * Проверка права в активном пространстве.
 *
 * Используется только для того, чтобы не показывать заведомо запрещённые
 * действия. Настоящая проверка выполняется на backend — скрытая кнопка
 * защитой не является.
 */
export function useHasPermission(permission: Permission): boolean {
  const workspace = useActiveWorkspace()
  return workspace?.permissions.includes(permission) ?? false
}

export function useIsAuthenticated(): boolean {
  return useAuthStore((state) => state.accessToken !== null && state.user !== null)
}
